import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import type { PartyType, RenderedTotals, StatementDetail, StatementLineItem } from "../../api/types";
import { useSession } from "../../auth/AuthProvider";
import { homeFor } from "../../auth/pages";
import { Banner, Button, Card, EmptyState, Money, PageHeader, QueryView, StatusPill, Table } from "../../components/ui";
import { formatLocal, formatPeriod } from "../../lib/datetime";
import { amendmentReasonText, disputesPath } from "./disputes/disputeCopy";
import { DisputeLineDialog } from "./disputes/DisputeLineDialog";

// Issued statements (ARCHITECTURE.md §10 layer 2; UX-DESIGN.md §2.5,
// §3.4, §1 "statements are immutable snapshots; corrections are
// amendments, not edits"). GET /v1/statements[/{id}] (statements.go) —
// both party types. A statement is shown exactly as issued: its stored
// rendered totals verbatim, its line items, and each amendment as a
// separate, linked entry — never a silently edited total.

function accountTypeLabel(t: string): string {
  return t.replace(/_/g, " ");
}

export function StatementsListPage() {
  const ep = useEndpoints();
  const session = useSession();
  const base = `${homeFor(session.partyType)}/statements`;
  const q = useQuery({ queryKey: ["statements"], queryFn: () => ep.statements.list() });

  return (
    <div className="stack-lg">
      <PageHeader
        title="Statements"
        description="Issued monthly statements are final snapshots. Corrections appear as amendments linked to the original — the original is never changed."
      />
      <QueryView query={q} loadingLabel="Loading statements" lines={5}>
        {(data) =>
          data.statements.length === 0 ? (
            <EmptyState icon="receipt" title="No statements issued yet">
              Statements are issued after each period closes. Live figures for the current period are on your{" "}
              {session.partyType === "advertiser" ? "campaign reports" : "earnings dashboard"}, labeled as not final.
            </EmptyState>
          ) : (
            <Table
              caption="Issued statements, newest first"
              captionHidden
              rows={data.statements}
              rowKey={(s) => s.statement_id}
              columns={[
                {
                  key: "period",
                  header: "Period",
                  cell: (s) => <Link to={`${base}/${s.statement_id}`}>{formatPeriod(s.period_start, s.period_end_exclusive)}</Link>,
                },
                { key: "issued", header: "Issued", cell: (s) => formatLocal(s.issued_at) },
                { key: "lines", header: "Line items", cell: (s) => s.line_count, numeric: true },
                {
                  key: "amend",
                  header: "Amendments",
                  cell: (s) =>
                    s.amendment_count > 0 ? (
                      <StatusPill tone="warning">{s.amendment_count === 1 ? "1 amendment" : `${s.amendment_count} amendments`}</StatusPill>
                    ) : (
                      <span className="muted">None</span>
                    ),
                },
              ]}
            />
          )
        }
      </QueryView>
      <p className="small muted">
        Each line can be disputed for 30 days after its statement is issued. Open a statement to see every line's deadline, or see{" "}
        <Link to={disputesPath(homeFor(session.partyType))}>your disputes</Link>.
      </p>
    </div>
  );
}

/**
 * G-47: a line's live dispute status (statements.go:139-147). The deadline
 * is shown on every line that has one, not only when filing (UX-DESIGN.md
 * §2.2, §3.6).
 */
function LineDisputeCell({ line, home, onDispute }: { line: StatementLineItem; home: string; onDispute: () => void }) {
  const until = line.dispute_eligible_until;
  switch (line.dispute_state) {
    case "eligible":
      return (
        <span className="stack-sm">
          <span className="xs">Open until {until ? formatLocal(until) : "—"}</span>
          <span>
            <Button size="sm" variant="secondary" onClick={onDispute} aria-label={`Dispute line ${line.line_no}`}>
              Dispute this line
            </Button>
          </span>
        </span>
      );
    case "window_closed":
      return <span className="xs muted">Window closed{until ? ` ${formatLocal(until)}` : ""}</span>;
    case "not_disputable":
      return <span className="xs muted">Not disputable</span>;
    case "open":
      return (
        <span className="stack-sm">
          <StatusPill tone="warning">Dispute open</StatusPill>
          {line.dispute_id ? <Link to={disputesPath(home, line.dispute_id)} className="xs">View dispute</Link> : null}
        </span>
      );
    case "resolved":
      return (
        <span className="stack-sm">
          <StatusPill tone={line.dispute_outcome === "upheld" ? "success" : "neutral"}>
            {line.dispute_outcome === "upheld" ? "Dispute upheld" : "Dispute denied"}
          </StatusPill>
          {line.dispute_id ? <Link to={disputesPath(home, line.dispute_id)} className="xs">View dispute</Link> : null}
        </span>
      );
  }
}

function disputeHint(partyType: PartyType): string {
  return partyType === "advertiser"
    ? "Only billed deliveries can be disputed; other lines say “Not disputable”."
    : "Any line can be disputed within its window.";
}

function TotalsTable({ totals, caption }: { totals: RenderedTotals; caption: string }) {
  if (!totals.currencies.length) return <p className="muted small">No totals on this document.</p>;
  return (
    <div className="stack">
      {totals.currencies.map((c) => (
        <div key={c.currency} className="stack-sm">
          <div className="row-between">
            <strong>
              Total ({c.currency}): <span className="money">{c.total.rounded}</span>
            </strong>
            <span className="xs muted">
              Exact: <code>{c.total.exact}</code>
            </span>
          </div>
          {c.by_account_type && c.by_account_type.length > 0 ? (
            <Table
              caption={`${caption} — ${c.currency} by account`}
              captionHidden
              rows={c.by_account_type}
              rowKey={(r) => r.account_type}
              columns={[
                { key: "type", header: "Account", cell: (r) => accountTypeLabel(r.account_type) },
                { key: "rounded", header: `Amount (${c.currency})`, cell: (r) => r.total.rounded, numeric: true },
                { key: "exact", header: "Exact", cell: (r) => <code className="xs">{r.total.exact}</code>, numeric: true },
              ]}
            />
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function StatementDetailPage() {
  const { statementId = "" } = useParams();
  const ep = useEndpoints();
  const session = useSession();
  const q = useQuery({ queryKey: ["statement", statementId], queryFn: () => ep.statements.get(statementId) });
  const home = homeFor(session.partyType);
  const [disputing, setDisputing] = useState<StatementLineItem | null>(null);

  return (
    <div className="stack-lg">
      <PageHeader title="Statement" back={{ to: `${homeFor(session.partyType)}/statements`, label: "All statements" }} />
      <QueryView query={q} loadingLabel="Loading statement" lines={8}>
        {(s: StatementDetail) => (
          <div className="stack-lg">
            <Card
              title={formatPeriod(s.period_start, s.period_end_exclusive)}
              subtitle={`Issued ${formatLocal(s.issued_at)}`}
              actions={<StatusPill tone="success" icon="lock">Issued snapshot</StatusPill>}
            >
              <div className="stack">
                <dl className="definition-list">
                  <dt>Statement ID</dt>
                  <dd>
                    <code>{s.statement_id}</code>
                  </dd>
                  <dt>Rounding policy</dt>
                  <dd>{s.rounding_policy_version} (half-even to each currency's minor units)</dd>
                  <dt>Sign convention</dt>
                  <dd>Positive amounts are in your favour</dd>
                </dl>
                <TotalsTable totals={s.rendered_totals} caption="Statement totals" />
                {s.amendments.length > 0 ? (
                  <Banner tone="warning" title={`This statement has ${s.amendments.length === 1 ? "an amendment" : `${s.amendments.length} amendments`}`}>
                    The totals above are the original issued figures. Corrections are listed below as separate amendments.{" "}
                    <a href="#amendments">Go to amendments</a>
                  </Banner>
                ) : null}
              </div>
            </Card>

            <Card
              title="Line items"
              subtitle={`Exact ledger amounts; totals above are rounded per the statement's policy. The dispute column is live: it changes as disputes are filed and decided. ${disputeHint(session.partyType)}`}
            >
              {s.line_items.length === 0 ? (
                <p className="muted small">No line items.</p>
              ) : (
                <Table
                  caption="Statement line items"
                  captionHidden
                  rows={s.line_items}
                  rowKey={(l) => l.line_item_id}
                  columns={[
                    { key: "no", header: "#", cell: (l) => l.line_no, numeric: true },
                    { key: "date", header: "Posted", cell: (l) => formatLocal(l.entry_created_at) },
                    { key: "desc", header: "Description", cell: (l) => l.description },
                    { key: "acct", header: "Account", cell: (l) => accountTypeLabel(l.account_type) },
                    { key: "amt", header: "Amount", cell: (l) => <Money value={l.amount} currency={l.currency} />, numeric: true },
                    {
                      key: "dispute",
                      header: "Dispute",
                      cell: (l) => <LineDisputeCell line={l} home={home} onDispute={() => setDisputing(l)} />,
                    },
                  ]}
                />
              )}
            </Card>
            {disputing ? (
              <DisputeLineDialog
                statementId={s.statement_id}
                line={disputing}
                partyType={session.partyType}
                home={home}
                onClose={() => setDisputing(null)}
              />
            ) : null}

            {s.amendments.length > 0 ? (
              <section className="stack" id="amendments" aria-labelledby="amendments-title">
                <h2 id="amendments-title">Amendments</h2>
                {s.amendments.map((a) => (
                  <Card
                    key={a.amendment_id}
                    id={`amendment-${a.amendment_id}`}
                    level={3}
                    title={`Amendment ${a.sequence_no}`}
                    subtitle={`Issued ${formatLocal(a.issued_at)} · amends the statement above`}
                  >
                    <div className="stack">
                      <div className="stack-sm">
                        <p>
                          <strong>Reason:</strong> {amendmentReasonText(a.reason)}.
                        </p>
                        {a.reason === "multiple" && a.reasons.length > 0 ? (
                          <ul className="small">
                            {a.reasons.map((r) => (
                              <li key={r}>{amendmentReasonText(r)}</li>
                            ))}
                          </ul>
                        ) : null}
                        {a.dispute_ids.length > 0 ? (
                          <p className="small">
                            {a.dispute_ids.length === 1 ? "Resolves " : "Resolves disputes: "}
                            {a.dispute_ids.map((id, i) => (
                              <span key={id}>
                                {i > 0 ? ", " : ""}
                                <Link to={disputesPath(home, id)}>{a.dispute_ids.length === 1 ? "your dispute" : `dispute ${i + 1}`}</Link>
                              </span>
                            ))}
                            .
                          </p>
                        ) : null}
                      </div>
                      <TotalsTable totals={a.rendered_totals} caption={`Amendment ${a.sequence_no} totals`} />
                      <Table
                        caption={`Amendment ${a.sequence_no} line items`}
                        captionHidden
                        rows={a.line_items}
                        rowKey={(l) => l.line_item_id}
                        columns={[
                          { key: "no", header: "#", cell: (l) => l.line_no, numeric: true },
                          { key: "type", header: "Type", cell: (l) => accountTypeLabel(l.line_type) },
                          { key: "desc", header: "Description", cell: (l) => l.description },
                          {
                            key: "corrects",
                            header: "Corrects line",
                            cell: (l) => {
                              if (!l.corrected_original_line_item_id) return <span className="muted">—</span>;
                              const orig = s.line_items.find((o) => o.line_item_id === l.corrected_original_line_item_id);
                              return orig ? `#${orig.line_no}` : <code className="xs">{l.corrected_original_line_item_id}</code>;
                            },
                          },
                          { key: "amt", header: "Amount", cell: (l) => <Money value={l.amount} currency={l.currency} />, numeric: true },
                        ]}
                      />
                    </div>
                  </Card>
                ))}
              </section>
            ) : null}
            <p className="small muted">
              Viewing a statement is recorded as account activity. A line can be disputed for 30 days after the statement is issued; a
              dispute filed after that is refused. Whether this statement was issued during a financial quarantine isn't reported yet
              (API-GAPS.md G-47).
            </p>
          </div>
        )}
      </QueryView>
    </div>
  );
}
